import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "config/axiosConfig";
import { getEntityDetails as fetchEntityDetails } from "util/getObjectDetails";
import { getWikipediaSummary } from "util/wikiUtils";
import { getIssueThumbnailUrl } from "util/entityUtils";
import { apiRoutes } from "util/apiRoutes";
import { isPostBackedKind } from "util/entityMetadata";
import type { Issue } from "util/constants";
import { logger } from "lib/logger";
import type { EntityAvatarCrosspostData, EntityAvatarEntity, EntityAvatarEntityType } from "./entityAvatar.types";
import { entityAvatarQueryKeys } from "./entityAvatarQueryKeys";
import { mergeEntityDetailWithInline } from "./entityDetailMerge";

export interface UseEntityAvatarDataParams {
  entity?: EntityAvatarEntity;
  entityId?: number | string;
  entityType: EntityAvatarEntityType;
  isInputField: boolean;
  crosspostData?: EntityAvatarCrosspostData;
  currentUserId: number | undefined;
}

export interface UseEntityAvatarDataResult {
  enrichedEntity: EntityAvatarEntity | Record<string, unknown> | null;
  crosspostTargetEntity: Record<string, unknown> | null;
  wikipediaSummary: Record<string, unknown> | null;
  resolvedIssueThumbnail: string | undefined;
  resolvedThumbByKey: Record<string, string>;
  isLoading: boolean;
  isError: boolean;
}

/**
 * Server-state for EntityAvatar: detail fetch, optional actor enrichment, Wikipedia + thumbnails, crosspost target.
 */
function parsePositiveEntityId(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value) && value > 0) {
    return value;
  }
  if (typeof value === "string" && /^\d+$/.test(value)) {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }
  return null;
}

export function useEntityAvatarData(params: UseEntityAvatarDataParams): UseEntityAvatarDataResult {
  const { entity, entityId, entityType, isInputField, crosspostData, currentUserId } = params;

  const resolvedEntityId = useMemo(() => {
    const inlineId =
      entity && typeof entity === "object" && "id" in entity
        ? (entity as Record<string, unknown>).id
        : undefined;
    return parsePositiveEntityId(entityId ?? inlineId);
  }, [entityId, entity]);

  const shouldFetchDetail = resolvedEntityId !== null;

  const detailQuery = useQuery({
    queryKey: shouldFetchDetail ? entityAvatarQueryKeys.detail(entityType, resolvedEntityId as number) : ["entityAvatar", "detail", "skip"],
    queryFn: async () => {
      const data = await fetchEntityDetails(entityType, resolvedEntityId as number, {
        includePlacementDetails: isPostBackedKind(entityType),
      });
      return data as Record<string, unknown>;
    },
    enabled: shouldFetchDetail,
  });

  /**
   * Prefer fetched detail, but merge inline `entity` (feed row) so denormalized fields
   * (e.g. `subject_cname`, `name`) are not lost when the detail payload is partial.
   * On fetch error, fall back to inline data so cards still render.
   */
  const baseEntity = useMemo(() => {
    const inline = (entity ?? null) as Record<string, unknown> | null;
    if (!shouldFetchDetail) {
      return inline;
    }
    if (detailQuery.isError && inline) {
      return inline;
    }
    const detail = detailQuery.data as Record<string, unknown> | undefined;
    if (!detail) {
      return inline;
    }
    if (inline) {
      return mergeEntityDetailWithInline(detail, inline, entityType);
    }
    return detail;
  }, [shouldFetchDetail, detailQuery.data, detailQuery.isError, entity, entityType]);

  const needsActorEnrichment =
    !isInputField &&
    baseEntity !== null &&
    parsePositiveEntityId(baseEntity.id) !== null &&
    (entityType === "user" || entityType === "team");

  const enrichQuery = useQuery({
    queryKey:
      entityType === "user"
        ? entityAvatarQueryKeys.enrichUser(parsePositiveEntityId(baseEntity?.id) ?? 0)
        : entityAvatarQueryKeys.enrichTeam(parsePositiveEntityId(baseEntity?.id) ?? 0),
    queryFn: async () => {
      const id = parsePositiveEntityId(baseEntity?.id);
      if (id === null) {
        throw new Error("ENTITY_AVATAR_ACTOR_ID_INVALID");
      }
      const response = await api.get(
        entityType === "user" ? apiRoutes.user.byId(id) : apiRoutes.team.byId(id)
      );
      const enriched: Record<string, unknown> = {
        ...baseEntity,
        username: entityType === "user" ? response.data.username : baseEntity?.username,
        teamname: entityType === "team" ? response.data.teamname : baseEntity?.teamname,
        display_name: response.data.display_name,
        properties_public: response.data.properties_public,
      };
      if (entityType === "team" && currentUserId) {
        const status = await api.get(apiRoutes.team.status(id));
        return { ...enriched, user_entity_details: status.data } as Record<string, unknown>;
      }
      if (entityType === "user" && currentUserId) {
        const status = await api.get(
          apiRoutes.follow.status("user", currentUserId, entityType, id)
        );
        return { ...enriched, user_entity_details: status.data } as Record<string, unknown>;
      }
      return enriched;
    },
    enabled: Boolean(needsActorEnrichment && baseEntity && parsePositiveEntityId(baseEntity.id) !== null),
  });

  const enrichedEntity = useMemo(() => {
    if (!baseEntity) {
      return null;
    }
    if (needsActorEnrichment && enrichQuery.data) {
      return enrichQuery.data;
    }
    return baseEntity;
  }, [baseEntity, needsActorEnrichment, enrichQuery.data]);
  const issueForWiki = enrichedEntity as unknown as Issue & { type?: string };
  const issueNameForWiki =
    entityType === "issue" && enrichedEntity
      ? issueForWiki.type !== "wiki" && typeof issueForWiki.name === "string"
        ? issueForWiki.name
        : null
      : null;

  const wikipediaQuery = useQuery({
    queryKey: issueNameForWiki ? entityAvatarQueryKeys.wikipediaSummary(issueNameForWiki) : ["entityAvatar", "wikipedia", "skip"],
    queryFn: async () => {
      const summary = await getWikipediaSummary(issueNameForWiki as string);
      return summary as unknown as Record<string, unknown>;
    },
    enabled: Boolean(issueNameForWiki),
  });

  const issueThumbQuery = useQuery({
    queryKey: issueNameForWiki ? entityAvatarQueryKeys.issueThumb(issueNameForWiki) : ["entityAvatar", "issueThumb", "skip"],
    queryFn: () => getIssueThumbnailUrl(issueNameForWiki as string),
    enabled: Boolean(issueNameForWiki),
  });

  const subjectNameForThumb = useMemo(() => {
    if (!enrichedEntity || typeof enrichedEntity !== "object") {
      return null;
    }
    const e = enrichedEntity as {
      placement?: { placement_kind?: string; subject_cname?: string; name?: string };
      subject_cname?: string;
      name?: string;
      related_issues?: Array<{ subject_cname?: string }>;
      related_issue?: { subject_cname?: string; name?: string };
    };
    const placement = e.placement;
    if (
      placement?.placement_kind === "issue" &&
      typeof placement.subject_cname === "string" &&
      placement.subject_cname.length > 0
    ) {
      return placement.subject_cname;
    }
    if (typeof e.subject_cname === "string" && e.subject_cname.length > 0) {
      return e.subject_cname;
    }
    const ri0 = Array.isArray(e.related_issues) ? e.related_issues[0] : undefined;
    if (ri0 && typeof ri0.subject_cname === "string" && ri0.subject_cname.length > 0) {
      return ri0.subject_cname;
    }
    if (e.related_issue && typeof e.related_issue.subject_cname === "string") {
      return e.related_issue.subject_cname;
    }
    if (
      placement?.placement_kind === "issue" &&
      typeof placement.name === "string" &&
      placement.name.length > 0
    ) {
      return placement.name;
    }
    return typeof e.name === "string" ? e.name : null;
  }, [enrichedEntity]);

  const subjectThumbQuery = useQuery({
    queryKey: subjectNameForThumb
      ? entityAvatarQueryKeys.issueThumb(`subject:${subjectNameForThumb}`)
      : ["entityAvatar", "subjectThumb", "skip"],
    queryFn: () => getIssueThumbnailUrl(subjectNameForThumb as string),
    enabled:
      Boolean(subjectNameForThumb) &&
      (entityType === "impact" ||
        entityType === "teabate" ||
        entityType === "solution" ||
        entityType === "action" ||
        entityType === "comment" ||
        entityType === "crosspost"),
  });

  const crosspostTargetQuery = useQuery({
    queryKey:
      crosspostData?.targetId !== undefined
        ? entityAvatarQueryKeys.crosspostTarget(String(crosspostData.targetType), crosspostData.targetId)
        : ["entityAvatar", "crosspost", "skip"],
    queryFn: async () => {
      if (!crosspostData?.targetId) {
        return null;
      }
      const response = await api.get(apiRoutes.entities.root, {
        params: {
          entityType: crosspostData.targetType,
          entityIds: crosspostData.targetId,
          limit: 1,
          offset: 0,
        },
      });
      const row = Array.isArray(response.data) && response.data.length > 0 ? response.data[0] : null;
      return row as Record<string, unknown> | null;
    },
    enabled:
      entityType === "crosspost" &&
      Boolean(crosspostData) &&
      crosspostData?.sourceType === "issue" &&
      crosspostData?.targetType === "issue" &&
      Boolean(crosspostData?.targetId) &&
      !crosspostData?.targetEntity,
  });

  const resolvedThumbByKey = useMemo(() => {
    const key = subjectNameForThumb;
    const url = subjectThumbQuery.data;
    if (key && typeof url === "string" && url.length > 0) {
      return { [key]: url };
    }
    return {};
  }, [subjectNameForThumb, subjectThumbQuery.data]);

  /** Block on required entity detail / actor enrichment; placement is included in detail fetch. */
  const isLoading =
    (shouldFetchDetail && detailQuery.isPending) ||
    (needsActorEnrichment && enrichQuery.isPending);

  const isError = shouldFetchDetail && detailQuery.isError;

  if (detailQuery.isError) {
    logger.error("entityAvatar.detailFetchFailed", {
      entityType,
      entityId: resolvedEntityId ?? entityId,
      message: detailQuery.error instanceof Error ? detailQuery.error.message : "unknown",
    });
  }

  return {
    enrichedEntity,
    crosspostTargetEntity: (crosspostTargetQuery.data ?? null) as Record<string, unknown> | null,
    wikipediaSummary: (wikipediaQuery.data ?? null) as Record<string, unknown> | null,
    resolvedIssueThumbnail: issueThumbQuery.data,
    resolvedThumbByKey,
    isLoading,
    isError,
  };
}
